import { api } from "@/lib/api";

import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";
import {
  PaymentAdjustmentDto,
  paymentAdjustmentDtoListSchema,
  PaymentAdjustmentDtoSchema,
  paymentAdjustmentFromDto,
} from "@/models/payment-adjustment";

export const getPaymentAdjustmentsQueryOptions = queryOptions({
  queryKey: ["payment-adjustments"],
  queryFn: async () => {
    const result: Array<PaymentAdjustmentDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.get(
        "/api/payment-adjustments",
        paymentAdjustmentDtoListSchema,
        {
          query: {
            page: page.toString(),
          },
          projection: ["PaymentAdjustmentReservationClients"],
        },
      );
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(paymentAdjustmentFromDto);
  },
});

export function useGetPaymentAdjustments<TERROR extends Error>(opts?: {
  onError?: (error: TERROR) => void;
  enabled?: boolean;
}) {
  const { toast } = useToast();

  const q = useQuery({
    ...getPaymentAdjustmentsQueryOptions,
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;

    toast({
      title: "toast.getPaymentAdjustmentsError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getPaymentAdjustmentQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["payment-adjustments", id],
    queryFn: () =>
      api
        .get("/api/payment-adjustments/$id", PaymentAdjustmentDtoSchema, {
          projection: ["PaymentAdjustmentReservationClients"],
          pathReplace: { $id: id },
        })
        .then(paymentAdjustmentFromDto),
  });
}
export function useGetPaymentAdjustment<TERROR extends Error>(
  id: string,
  opts?: {
    onError?: (error: TERROR) => void;
    enabled?: boolean;
  },
) {
  const { toast } = useToast();

  const q = useQuery({
    ...getPaymentAdjustmentQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getPaymentAdjustmentsError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
